<template>
  <div
    id="business_details"
    class="flex flex-col gap-6 secondary_color proxima_nova"
  >
    <h1 class="text-xl text_color font-semibold">Business Details</h1>

    <div class="flex items-center gap-4">
      <ProfileIcon
        :key="formState.business_name + (profileImage ? 'image' : 'initials')"
        :data="
          profileImage
            ? { company: formState.business_name, profile_pic: profileImage }
            : formState.business_name
        "
        :size="60"
        :font="22"
        class="rounded-full"
      />

      <div class="flex items-center gap-2">
        <a-button
          class="secondary_medium flex justify-center"
          @click="handleUploadClick"
        >
          <ThemeImage
            :icon="imageNames.UPLOAD_1_GREY"
            alt="upload_icon"
            class="size-3 mr-1"
          />
          <span class="font-semibold">Upload</span>
        </a-button>

        <a-button
          v-if="profileImage"
          size="middle"
          class="secondary_medium flex justify-center"
          @click="handleRemoveImage"
        >
          <ThemeImage
            :icon="imageNames.CROSS_GREY"
            alt="cross_icon"
            class="size-4"
          />
        </a-button>
      </div>

      <input
        type="file"
        ref="fileInput"
        class="hidden"
        @change="handleFileChange"
        accept="image/*"
      />
    </div>

    <div class="flex flex-col gap-6 secondary_color text-[13px] font-normal">
      <div class="form_col">
        <span>Business Name</span>
        <CustomInput
          format="text"
          v-model="formState.business_name"
          placeholder="Enter Business Name"
          type="primary"
          :isError="!!errors.business_name"
          :errorMessage="errors.business_name"
          auto-focus
        />
      </div>

      <div class="form_col">
        <span>Description</span>
        <a-textarea
          v-model:value="formState.business_description"
          rows="4"
          placeholder="Enter a description of your business"
          class="textarea_medium"
        />
        <span v-if="errors.business_description" class="error-message">
          {{ errors.business_description }}
        </span>
      </div>

      <div class="form_col">
        <span>Website</span>
        <CustomInput
          format="text"
          v-model="formState.business_website"
          placeholder="Enter Website"
          type="primary"
          :isError="!!errors.business_website"
          :errorMessage="errors.business_website"
        />
      </div>

      <div class="form_col" v-if="showMeetingLinkInput">
        <span>Default Workspace Meeting Link</span>
        <CustomInput
          format="text"
          v-model="formState.workspace_meeting_link"
          placeholder="Enter default meeting link"
          type="primary"
          :isError="!!errors.workspace_meeting_link"
          :errorMessage="errors.workspace_meeting_link"
        />
      </div>

      <div class="form_col">
        <span>Phone Number</span>
        <vue-tel-input
          v-model="formState.business_phone_number"
          :defaultCountry="defaultCountryCode"
          :dropdownOptions="{
            showFlags: true,
            showDialCodeInList: true,
            showSearchBox: true,
            searchBoxPlaceholder: 'Search country..',
          }"
          mode="international"
          :inputOptions="{ placeholder: 'Enter Phone Number' }"
          :class="{ 'error-border': !!errors.business_phone_number }"
          @blur="validateBusinessPhoneNumber"
          @update:model-value="validateBusinessPhoneNumber"
        />
        <span
          v-if="errors.business_phone_number"
          class="text-xs error_message_color mt-1"
        >
          {{ errors.business_phone_number }}
        </span>
      </div>

      <div class="form_col">
        <span>Address</span>
        <a-textarea
          v-model:value="formState.business_address"
          rows="3"
          placeholder="Enter the full business address"
          class="textarea_medium"
        />
      </div>

      <h1 class="text-xl text_color font-semibold">ICP</h1>

      <div class="form_col">
        <span>Define your ideal customer profile</span>
        <a-textarea
          v-model:value="formState.business_icp"
          rows="4"
          placeholder="Enter a description of your business"
          class="textarea_medium"
        />
        <span v-if="errors.business_icp" class="error-message">
          {{ errors.business_icp }}
        </span>
      </div>

      <a-button
        @click="handleSubmit"
        class="primary_medium w-fit"
        :loading="updating"
      >
        {{ updating ? "Updating" : "Update Changes" }}
      </a-button>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted, computed } from "vue";
import { message } from "ant-design-vue";
import { useStore } from "vuex";
import { VueTelInput } from "vue-tel-input";
import DashboardService from "@/src/api/DashboardService.js";
import CustomInput from "@/src/components/CustomInput.vue";
import { showMeetingLinkInput } from "@/src/utils/utils.js";
import { UploadOutlined, CloseOutlined } from "@ant-design/icons-vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import { sanitizeInput } from "@/src/utils/utils.js";
import { isValidPhone } from "@/src/utils/phoneNumberUtils.js";
import imagePath from "@/src/paths/imagePath.js";
import { imageNames } from "@/src/constants/imageConstants.js";

const store = useStore();
const defaultCountryCode = computed(
  () => store.getters["workspace/defaultCountryCode"],
);

const fileInput = ref(null);
const profileImage = ref(null);
const currentWorkspaceId = ref("");
const imageRemoved = ref(false);

const entity = ref({
  company: " ",
  profile_pic: null,
});

const handleUploadClick = () => {
  fileInput.value?.click();
};

const handleFileChange = async (event) => {
  const file = event.target.files[0];
  if (file) {
    if (file.size > 5 * 1024 * 1024) {
      message.error("Image size should be less than 5MB");
      return;
    }

    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result && reader.result.startsWith("data:image/")) {
          const imageData = reader.result;
          profileImage.value = imageData;
          formState.profile_image = imageData;
        } else {
          message.error("Invalid image format");
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      message.error("Failed to load image");
    }
  }
  event.target.value = "";
};

const handleRemoveImage = () => {
  profileImage.value = null;
  formState.profile_image = null;
  imageRemoved.value = true;

  entity.value = {
    ...entity.value,
    company: entity.value.company || "",
    profile_pic: null,
  };
};

const emit = defineEmits(["business-updated", "business-added", "cancel"]);

const initialFormState = reactive({
  business_name: "",
  business_description: "",
  business_icp: "",
  business_website: "",
  business_address: "",
  business_phone_number: "",
  workspace_meeting_link: "",
  profile_image: null,
});

const formState = reactive({
  business_name: "",
  business_description: "",
  business_icp: "",
  business_website: "",
  business_address: "",
  business_phone_number: "",
  workspace_meeting_link: "",
  profile_image: null,
});

const updating = ref(false);
const loading = ref(true);

const isEditing = ref(true);
const errors = reactive({
  business_name: "",
  business_description: "",
  business_icp: "",
  business_website: "",
  business_phone_number: "",
  workspace_meeting_link: "",
});

const validateBusinessPhoneNumber = () => {
  const phone = formState.business_phone_number;
  if (!phone) {
    errors.business_phone_number = "";
    return true;
  }
  if (!isValidPhone(phone, defaultCountryCode.value)) {
    errors.business_phone_number = "Please enter a valid phone number";
    return false;
  }
  errors.business_phone_number = "";
  return true;
};

const validateMeetingLink = (link) => {
  const meetingLinkRegex =
    /^https:\/\/meetings(?:-[a-zA-Z0-9]+)?\.superagi\.com\/[\w-]+\/[\w-]+$/i;
  return meetingLinkRegex.test(link);
};

const validateForm = () => {
  let isValid = true;

  if (initialFormState.business_name && !formState.business_name) {
    errors.business_name = "Business name cannot be cleared";
    isValid = false;
  } else {
    errors.business_name = "";
  }

  if (
    initialFormState.business_description &&
    !formState.business_description
  ) {
    errors.business_description = "Business description cannot be cleared";
    isValid = false;
  } else {
    errors.business_description = "";
  }

  if (initialFormState.business_website && !formState.business_website) {
    errors.business_website = "Website URL cannot be cleared";
    isValid = false;
  } else {
    errors.business_website = "";
  }

  if (
    initialFormState.workspace_meeting_link &&
    !formState.workspace_meeting_link
  ) {
    errors.workspace_meeting_link = "Meeting link cannot be cleared";
    isValid = false;
  } else if (
    formState.workspace_meeting_link &&
    !validateMeetingLink(formState.workspace_meeting_link)
  ) {
    errors.workspace_meeting_link =
      "The link entered is invalid. Please enter a SuperAGI meet link";
    isValid = false;
  } else {
    errors.workspace_meeting_link = "";
  }

  if (initialFormState.business_icp && !formState.business_icp) {
    errors.business_icp = "Business icp cannot be cleared";
    isValid = false;
  } else {
    errors.business_icp = "";
  }

  if (!validateBusinessPhoneNumber()) {
    isValid = false;
  }

  return isValid;
};

const sanitizeForm = () => {
  const fields = [
    "business_name",
    "business_description",
    "business_icp",
    "business_website",
    "business_address",
    "business_phone_number",
    "workspace_meeting_link",
  ];

  return fields.every((field) => {
    const value = formState[field];

    if (!value) return true;

    return (
      sanitizeInput(value) === value ||
      (field === "workspace_meeting_link" && !showMeetingLinkInput)
    );
  });
};

const fetchBusinessDetails = async () => {
  try {
    const response = await DashboardService.getBusinessDetails();
    if (response.success && response.business) {
      Object.assign(initialFormState, response.business);
      Object.assign(formState, response.business);

      currentWorkspaceId.value = response.business.id || "";
      const imageID = response.business.id;

      if (imageID) {
        try {
          const downloadResponse =
            await DashboardService.downloadProfileImage(imageID);
          if (downloadResponse.data) {
            const mimeType = getMimeTypeFromBase64(downloadResponse.data);
            profileImage.value = `data:${mimeType};base64,${downloadResponse.data}`;
          } else {
            profileImage.value = null;
          }
        } catch (downloadError) {
          console.error("Error downloading profile image:", downloadError);
          profileImage.value = null;
        }
      } else {
        profileImage.value = null;
      }

      entity.value = {
        company: response.business.business_name || "",
        profile_pic: profileImage.value || null,
      };
      isEditing.value = true;
    }
  } catch (error) {
    console.error("Error fetching business details:", error);
  } finally {
    loading.value = false;
  }
};

const handleSubmit = async () => {
  Object.keys(formState).forEach((key) => {
    if (typeof formState[key] === "string") {
      formState[key] = formState[key].trim();
    }
  });

  if (!validateForm()) {
    message.error("Please correct the errors in the form");
    return;
  }

  if (!sanitizeForm()) {
    message.error("Invalid characters detected");
    return;
  }

  try {
    updating.value = true;

    if (imageRemoved.value && currentWorkspaceId.value) {
      const removeRes = await DashboardService.removeProfileImage(
        currentWorkspaceId.value,
      );
      if (!removeRes.success) {
        message.error("Failed to remove profile image");
        updating.value = false;
        return;
      }
    }

    if (
      formState.profile_image &&
      formState.profile_image !== initialFormState.profile_image
    ) {
      const uploadRes = await DashboardService.uploadProfileImage(formState);
      if (!uploadRes.success) {
        message.error("Failed to upload profile image");
        updating.value = false;
        return;
      }
    }

    const response = await DashboardService.updateBusinessDetails(formState);
    if (response?.success) {
      message.success("Business details updated successfully");
      emit("business-added", response?.business);
      if (formState.workspace_meeting_link) {
        localStorage.setItem(
          "workspaceMeetingLink",
          formState.workspace_meeting_link,
        );
      }
      Object.assign(initialFormState, formState);
      imageRemoved.value = false;
    }
  } catch (error) {
    console.error("Error updating business details:", error);
    message.error(
      error?.response?.data?.error ?? "Failed to update business details",
    );
  } finally {
    updating.value = false;
  }
};

const getMimeTypeFromBase64 = (base64String) => {
  const match = base64String.match(/^data:(image\/[a-zA-Z]+);base64,/);
  return match ? match[1] : null;
};

onMounted(() => {
  fetchBusinessDetails();
});
</script>
